// THE HOME PAGE A/B (owner, 2026-10-25: "do the A/B with modular type").
// ---------------------------------------------------------------------------
// Two arms of `/`: `control` (pages/home.tsx as it stood) and `modular`
// (pages/home-modular.tsx, the 12-column, four-size rebuild that ranked first
// in the 2026-10-25 blind read). Same copy, same drawn screens, same section
// ids and CTA positions, so the existing funnel reads both arms; only the
// layout differs. The measure is `store_badge_click` per visitor, broken down
// by the `home_arm` super property (docs/ops/web-funnel.md §6).
//
// WHY NOT getVariant(). A PostHog flag resolves after the page has painted,
// and a whole-page layout swapped in after paint is the flash §6 rule 1
// forbids; it would also hand the modular arm a slower first paint, which
// biases the test against it. So the coin is tossed BEFORE first paint, in the
// page's own <head>, and both arms are in the prerendered document:
//
//   1. prerender.mjs renders `/` twice. The control arm is the page as served;
//      the modular arm goes into an inert <template> after #root, with no
//      resource hints, so a control visitor downloads none of its pictures.
//   2. HOME_ARM_HEAD_SCRIPT, inline in `/`'s <head>, picks the arm and sets
//      `data-home-arm` on <html>. HOME_ARM_HIDE_CSS keeps #root unpainted
//      while the arm is modular and the swap has not happened yet.
//   3. HOME_ARM_SWAP_SCRIPT, inline right after the template, moves the
//      template's nodes into #root during parsing, so the browser's first
//      paint is already the modular arm.
//   4. readHomeArm() reads the same attribute on the client, so pages/home.tsx
//      renders the arm the DOM holds and hydration matches.
//
// WHO IS IN THE TEST. A visitor whose first load of `/` is a full page load.
// Do Not Track, Global Privacy Control, automation (`navigator.webdriver`, so
// every gate in CI and verify-hydration renders the control) and crawlers get
// the control and are never stored, so nothing about them is recorded. A
// client-side navigation to `/` uses the stored arm or the control.
//
// WHERE THE COIN IS KEPT. sessionStorage, the same tab-scoped place the default
// analytics tier keeps its anonymous id; localStorage only when the visitor
// accepted the consent bar, matching the accepted tier's persistence, so a
// returning visitor who is one PostHog id is also one arm. The value is the
// word "control" or "modular" and nothing else.
//
// `?home_arm=modular` (or `control`) on `/` forces and stores an arm. It is
// for QA and for the hydration gate; nothing on the site links to it.
//
// ENDING THE TEST: set HOME_ARM_TEST to false (every visitor gets the control
// and prerender.mjs stops emitting the template), or delete the losing arm.
// Either way it ships with the next web Republish.

export type HomeArm = "control" | "modular";

/** The switch. False: no template, no head script, everyone sees the control. */
export const HOME_ARM_TEST: boolean = true;

/** The storage key (session or local, see the header). */
export const HOME_ARM_KEY = "capnolog_home_arm";

/** The element id of the prerendered modular arm. */
export const HOME_ARM_TEMPLATE_ID = "home-arm-modular";

export function isHomeArm(value: unknown): value is HomeArm {
  return value === "control" || value === "modular";
}

// ── the three inline pieces prerender.mjs stamps into `/` ──────────────────
// ES5 and self-contained: they run before any bundle, in every browser that
// can render the page. Every storage call sits inside the try, because a
// private window or blocked site data throws on access; a throw leaves the
// control.

/**
 * Picks the arm. The opt-out test is privacySignalsOptOut() in analytics.ts,
 * restated: keep the two in step. An arm found in sessionStorage is written
 * back through the tier choice, so a visitor who accepted since is moved to
 * localStorage on their next load of `/`.
 */
export const HOME_ARM_HEAD_SCRIPT = `(function(){var d=document.documentElement,k=${JSON.stringify(HOME_ARM_KEY)},a=null,keep=false;try{var q=/[?&]home_arm=(control|modular)(?:&|$)/.exec(location.search);if(q){a=q[1];keep=true}if(!a){a=localStorage.getItem(k);if(!a){a=sessionStorage.getItem(k);keep=!!a}}if(a!=="control"&&a!=="modular"){a="control";var n=navigator,w=window,t=n.doNotTrack||w.doNotTrack||n.msDoNotTrack,opted=t==="1"||t==="yes"||n.globalPrivacyControl===true,robot=n.webdriver===true||/bot|crawl|spider|slurp|lighthouse|headless|preview/i.test(n.userAgent||"");if(!opted&&!robot){a=Math.random()<0.5?"control":"modular";keep=true}}if(keep){var c=null;try{c=JSON.parse(localStorage.getItem("capnolog_consent")||"null")}catch(e){}(c&&c.choice==="granted"?localStorage:sessionStorage).setItem(k,a)}}catch(e){if(a!=="modular")a="control"}d.setAttribute("data-home-arm",a)})();`;

/** Holds #root unpainted while the modular arm waits for its swap. */
export const HOME_ARM_HIDE_CSS = `html[data-home-arm="modular"] #root:not([data-home-arm="modular"]){visibility:hidden}`;

/**
 * Moves the template into #root. A missing template (a host that served `/`'s
 * file for another URL) falls back to the control, which is what #root holds.
 */
export const HOME_ARM_SWAP_SCRIPT = `(function(){var d=document.documentElement;if(d.getAttribute("data-home-arm")!=="modular")return;var t=document.getElementById(${JSON.stringify(HOME_ARM_TEMPLATE_ID)}),r=document.getElementById("root");if(!t||!r||!t.content){d.setAttribute("data-home-arm","control");return}while(r.firstChild)r.removeChild(r.firstChild);r.appendChild(t.content);r.setAttribute("data-home-arm","modular");t.parentNode.removeChild(t)})();`;

// ── reading the arm ─────────────────────────────────────────────────────────

let serverArm: HomeArm = "control";

/** prerender.mjs, through entry-server's render(): which arm to render. */
export function setServerHomeArm(arm: HomeArm): void {
  serverArm = arm;
}

/** The arm this visitor was assigned and stored, or null when there is none. */
export function storedHomeArm(): HomeArm | null {
  if (!HOME_ARM_TEST || typeof window === "undefined") return null;
  try {
    const value =
      window.localStorage.getItem(HOME_ARM_KEY) ??
      window.sessionStorage.getItem(HOME_ARM_KEY);
    return isHomeArm(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * The consent bar's answer moves the stored arm with the counting id (called by
 * setConsent in analytics.ts): Accept copies it into localStorage, Decline
 * moves it back into this tab's sessionStorage and clears the local copy.
 */
export function moveHomeArmToTier(granted: boolean): void {
  if (!HOME_ARM_TEST || typeof window === "undefined") return;
  try {
    const arm = storedHomeArm();
    if (!arm) return;
    if (granted) {
      window.localStorage.setItem(HOME_ARM_KEY, arm);
    } else {
      window.sessionStorage.setItem(HOME_ARM_KEY, arm);
      window.localStorage.removeItem(HOME_ARM_KEY);
    }
  } catch {
    // Blocked storage: the arm simply stays where it is.
  }
}

/**
 * The arm to render. On the server, the one prerender.mjs asked for. On the
 * client, the attribute the head script set (which is what the DOM holds, so
 * hydration matches), else the stored arm, else the control.
 */
export function readHomeArm(): HomeArm {
  if (!HOME_ARM_TEST) return "control";
  if (typeof document === "undefined") return serverArm;
  const attr = document.documentElement.getAttribute("data-home-arm");
  if (isHomeArm(attr)) return attr;
  return storedHomeArm() ?? "control";
}
